/**
 * BulkPromotionAppointmentModal 컴포넌트
 *
 * 진급 후보를 일괄 승진 발령 처리하는 모달입니다.
 *
 * **기능:**
 * - 선택된 진급 후보 목록 표시
 * - 공통 발령일/메모 입력
 * - 일괄 승진 발령 생성
 * - 성공/실패 결과 피드백
 *
 * **사용 예시:**
 * ```tsx
 * <BulkPromotionAppointmentModal
 *   isOpen={isModalOpen}
 *   candidates={selectedCandidates}
 *   referenceDate="2025-01-01"
 *   onClose={handleClose}
 *   onSuccess={handleSuccess}
 * />
 * ```
 */

import React, { useState, useEffect } from 'react';
import { X, Loader, CheckCircle, AlertCircle, Users } from 'lucide-react';
import { useModalOverlayClose } from '../../../shared/hooks/useModalOverlayClose';
import { PromotionCandidate } from '../../../shared/types/promotionCandidate';
import { useBulkPromotionAppointments } from '../../../shared/hooks/useBulkPromotionAppointments';

interface BulkPromotionAppointmentModalProps {
  /** 모달 열림 여부 */
  isOpen: boolean;

  /** 선택된 진급 후보 목록 */
  candidates: PromotionCandidate[];

  /** 기준일 (발령일 기본값으로 사용) */
  referenceDate?: string;

  /** 모달 닫기 콜백 */
  onClose: () => void;

  /** 저장 성공 시 콜백 */
  onSuccess: (successCount: number, failureCount: number) => void;
}

/**
 * 일괄 승진 발령 모달 컴포넌트
 */
export function BulkPromotionAppointmentModal({
  isOpen,
  candidates,
  referenceDate,
  onClose,
  onSuccess,
}: BulkPromotionAppointmentModalProps) {
  const overlayProps = useModalOverlayClose(onClose);

  // Hook
  const { createPromotionAppointmentsFromCandidates, isSubmitting } =
    useBulkPromotionAppointments();

  // 폼 상태
  const [effectiveDate, setEffectiveDate] = useState(
    referenceDate || getTodayDateString()
  );
  const [note, setNote] = useState('');

  // 에러/성공 메시지
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [resultMessage, setResultMessage] = useState<string | null>(null);

  // 모달이 열릴 때 초기값 설정
  useEffect(() => {
    if (isOpen) {
      setEffectiveDate(referenceDate || getTodayDateString());
      setNote('');
      setErrorMessage(null);
      setResultMessage(null);
    }
  }, [isOpen, referenceDate]);

  /**
   * 폼 제출 핸들러
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setResultMessage(null);

    // 필수 필드 검증
    if (!effectiveDate) {
      setErrorMessage('발령일을 입력해주세요.');
      return;
    }

    if (candidates.length === 0) {
      setErrorMessage('진급 발령 처리할 대상이 없습니다.');
      return;
    }

    try {
      // 일괄 승진 발령 생성
      const result = await createPromotionAppointmentsFromCandidates(candidates, {
        effectiveDate,
        note: note.trim() || undefined,
      });

      // 결과 메시지
      if (result.failureCount === 0) {
        // 전체 성공
        setResultMessage(
          `✅ 총 ${result.successCount}건의 승진 발령이 성공적으로 생성되었습니다.`
        );
      } else if (result.successCount === 0) {
        // 전체 실패
        setErrorMessage(
          `❌ 전체 ${result.failureCount}건의 승진 발령이 모두 실패했습니다.`
        );
      } else {
        // 부분 성공
        setResultMessage(
          `⚠️ ${result.successCount}건 성공, ${result.failureCount}건 실패했습니다.\n\n실패 목록:\n${result.failures.map(f => `- ${f.employeeName}: ${f.error}`).join('\n')}`
        );
      }

      // 성공 콜백 호출
      onSuccess(result.successCount, result.failureCount);

      // 1초 후 모달 닫기 (사용자가 결과 메시지를 볼 시간 제공)
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      console.error('일괄 승진 발령 생성 실패:', err);
      setErrorMessage(err.message || '일괄 승진 발령 생성에 실패했습니다.');
    }
  };

  // 모달이 닫혀있으면 렌더링하지 않음
  if (!isOpen) {
    return null;
  }

  // 직위별 후보자 수 계산 (요약용)
  const positionSummary = candidates.reduce((acc, candidate) => {
    const key = `${candidate.currentPosition.name} → ${candidate.nextPosition?.name || '?'}`;
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div style={styles.overlay} {...overlayProps}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        {/* 헤더 */}
        <div style={styles.header}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Users size={24} color="var(--color-primary)" />
            <h2 style={styles.title}>일괄 승진 발령 생성</h2>
          </div>
          <button style={styles.closeButton} onClick={onClose} disabled={isSubmitting}>
            <X size={20} />
          </button>
        </div>

        {/* 에러 메시지 */}
        {errorMessage && (
          <div style={styles.errorBox}>
            <AlertCircle size={20} />
            <p style={styles.errorText}>{errorMessage}</p>
          </div>
        )}

        {/* 성공 메시지 */}
        {resultMessage && (
          <div style={styles.successBox}>
            <CheckCircle size={20} />
            <p style={styles.successText}>{resultMessage}</p>
          </div>
        )}

        {/* 요약 정보 */}
        <div style={styles.summaryBox}>
          <h3 style={styles.summaryTitle}>발령 대상 요약</h3>
          <div style={styles.summaryContent}>
            <div style={styles.summaryItem}>
              <span style={styles.summaryLabel}>총 인원</span>
              <span style={styles.summaryValue}>{candidates.length}명</span>
            </div>
            {Object.entries(positionSummary).map(([key, count]) => (
              <div key={key} style={styles.summaryItem}>
                <span style={styles.summaryLabel}>{key}</span>
                <span style={styles.summaryValue}>{count}명</span>
              </div>
            ))}
          </div>
        </div>

        {/* 폼 */}
        <form onSubmit={handleSubmit} style={styles.form}>
          {/* 발령일 (필수) */}
          <div style={styles.formGroup}>
            <label style={styles.label}>
              발령일 <span style={{ color: 'var(--color-error)' }}>*</span>
            </label>
            <input
              type="date"
              value={effectiveDate}
              onChange={e => setEffectiveDate(e.target.value)}
              style={styles.input}
              required
              disabled={isSubmitting}
            />
            <p style={styles.helpText}>
              모든 대상자에게 동일한 발령일이 적용됩니다.
            </p>
          </div>

          {/* 메모 (선택) */}
          <div style={styles.formGroup}>
            <label style={styles.label}>메모 (선택)</label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="예: 2025년 정기 인사발령"
              style={styles.textarea}
              rows={3}
              disabled={isSubmitting}
            />
            <p style={styles.helpText}>모든 발령에 동일한 메모가 기록됩니다.</p>
          </div>

          {/* 대상자 리스트 */}
          <div style={styles.formGroup}>
            <label style={styles.label}>발령 대상자 목록</label>
            <div style={styles.candidateListContainer}>
              <table style={styles.candidateTable}>
                <thead>
                  <tr>
                    <th style={styles.tableHeader}>이름</th>
                    <th style={styles.tableHeader}>조직</th>
                    <th style={styles.tableHeader}>현재 직위</th>
                    <th style={styles.tableHeader}>다음 직위</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map(candidate => (
                    <tr key={candidate.employee.id} style={styles.tableRow}>
                      <td style={styles.tableCell}>{candidate.employee.nameKo}</td>
                      <td style={styles.tableCell}>
                        {candidate.employee.orgUnitName || '-'}
                      </td>
                      <td style={styles.tableCell}>{candidate.currentPosition.name}</td>
                      <td style={styles.tableCell}>
                        <strong>{candidate.nextPosition?.name || '-'}</strong>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* 안내 메시지 */}
          <div style={styles.infoBox}>
            <p style={styles.infoText}>
              💡 승진 발령 생성 시 각 직원의 <strong>현재 직위</strong> 및{' '}
              <strong>직위 발령일</strong>이 자동으로 업데이트됩니다.
            </p>
          </div>

          {/* 버튼 */}
          <div style={styles.buttonGroup}>
            <button
              type="button"
              onClick={onClose}
              style={styles.cancelButton}
              disabled={isSubmitting}
            >
              취소
            </button>
            <button
              type="submit"
              style={{
                ...styles.submitButton,
                opacity: isSubmitting ? 0.6 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  <span>생성 중...</span>
                </>
              ) : (
                <span>승진 발령 생성</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * 오늘 날짜를 YYYY-MM-DD 형식으로 반환
 */
function getTodayDateString(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * 스타일
 */
const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: 'white',
    borderRadius: '12px',
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '24px',
    borderBottom: '1px solid var(--color-gray-200)',
  },
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    color: 'var(--color-gray-800)',
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    color: 'var(--color-gray-500)',
    transition: 'color 0.2s',
  },
  errorBox: {
    margin: '16px 24px 0',
    padding: '12px 16px',
    backgroundColor: '#fee',
    borderRadius: '8px',
    border: '1px solid var(--color-error)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  errorText: {
    color: 'var(--color-error)',
    fontSize: '14px',
    margin: 0,
    flex: 1,
  },
  successBox: {
    margin: '16px 24px 0',
    padding: '12px 16px',
    backgroundColor: '#e8f5e9',
    borderRadius: '8px',
    border: '1px solid #4caf50',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  successText: {
    color: '#2e7d32',
    fontSize: '14px',
    margin: 0,
    flex: 1,
    whiteSpace: 'pre-line' as const,
  },
  summaryBox: {
    margin: '16px 24px 0',
    padding: '16px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    border: '1px solid var(--color-gray-200)',
  },
  summaryTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--color-gray-700)',
    marginBottom: '12px',
  },
  summaryContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px',
  },
  summaryItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '13px',
  },
  summaryLabel: {
    color: 'var(--color-gray-600)',
  },
  summaryValue: {
    color: 'var(--color-gray-800)',
    fontWeight: '600',
  },
  infoBox: {
    margin: '16px 0',
    padding: '12px 16px',
    backgroundColor: '#e3f2fd',
    borderRadius: '8px',
    border: '1px solid #2196f3',
  },
  infoText: {
    color: '#1976d2',
    fontSize: '13px',
    margin: 0,
    lineHeight: '1.5',
  },
  form: {
    padding: '24px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--color-gray-700)',
    marginBottom: '8px',
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '6px',
    outline: 'none',
    transition: 'border-color 0.2s',
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '6px',
    outline: 'none',
    resize: 'vertical' as const,
    fontFamily: 'inherit',
  },
  helpText: {
    fontSize: '12px',
    color: 'var(--color-gray-500)',
    marginTop: '4px',
    margin: 0,
  },
  candidateListContainer: {
    maxHeight: '300px',
    overflowY: 'auto' as const,
    border: '1px solid var(--color-gray-200)',
    borderRadius: '6px',
  },
  candidateTable: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  tableHeader: {
    padding: '12px',
    textAlign: 'left' as const,
    fontSize: '12px',
    fontWeight: '600',
    color: 'var(--color-gray-700)',
    backgroundColor: 'var(--color-gray-50)',
    borderBottom: '1px solid var(--color-gray-200)',
    position: 'sticky' as const,
    top: 0,
  },
  tableRow: {
    borderBottom: '1px solid var(--color-gray-200)',
  },
  tableCell: {
    padding: '12px',
    fontSize: '13px',
    color: 'var(--color-gray-800)',
  },
  buttonGroup: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    marginTop: '24px',
    paddingTop: '16px',
    borderTop: '1px solid var(--color-gray-200)',
  },
  cancelButton: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--color-gray-700)',
    backgroundColor: 'white',
    border: '1px solid var(--color-gray-300)',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  submitButton: {
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: '500',
    color: 'white',
    backgroundColor: 'var(--color-primary)',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
};
